'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import PageHero from '@/components/layout/PageHero';
import { ChevronDown, Search, HelpCircle, FileText, Users, DollarSign, Shield, Upload, BookOpen } from 'lucide-react';
import PageTransition, { animationClasses, animationDelays } from '@/components/PageTransition';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  icon: React.ReactNode;
}

export default function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const faqItems: FAQItem[] = [
    {
      id: 'what-is-indiestry',
      question: 'What is Indiestry?',
      answer: 'Indiestry is a comprehensive social platform designed specifically for voice actors and audio professionals. We combine job search tools, networking features, live streaming capabilities, and portfolio-building tools all in one creative hub. Our mission is to help voice actors connect, learn, and grow their careers in the industry.',
      category: 'General',
      icon: <HelpCircle className="w-5 h-5" />
    },
    {
      id: 'upload-portfolio',
      question: 'How do I upload a portfolio?',
      answer: 'To upload your portfolio, navigate to your profile page and click on the "Portfolio" tab. You can upload audio files (MP3, WAV), video files (MP4), and images (JPG, PNG). Each file should be under 50MB. We recommend including a variety of work samples to showcase your range and versatility.',
      category: 'Portfolio',
      icon: <Upload className="w-5 h-5" />
    },
    {
      id: 'paid-jobs',
      question: 'Are there paid job listings?',
      answer: 'Yes! Indiestry features both paid and unpaid opportunities. We have a robust job board with verified paid listings from reputable clients and studios. All paid jobs are clearly marked with budget ranges, and we encourage transparency in payment terms. We also offer revenue-sharing opportunities for long-term projects.',
      category: 'Jobs',
      icon: <DollarSign className="w-5 h-5" />
    },
    {
      id: 'free-workshops',
      question: 'Can I take workshops for free?',
      answer: 'Absolutely! We offer a mix of free and paid educational content. Many of our community members host free workshops and learning sessions to share their expertise. You can also access our resource library with free tutorials, warm-up exercises, and industry tips. Premium workshops are available for more in-depth training.',
      category: 'Education',
      icon: <BookOpen className="w-5 h-5" />
    },
    {
      id: 'file-types',
      question: 'What file types can I submit for auditions?',
      answer: 'For auditions, we accept MP3 and WAV audio files (recommended: 44.1kHz, 16-bit or higher). Video files should be MP4 format. Image files can be JPG or PNG. All files should be clearly labeled with your name and the project title. We recommend keeping audio files under 10MB for faster uploads.',
      category: 'Technical',
      icon: <FileText className="w-5 h-5" />
    },
    {
      id: 'report-violations',
      question: 'How do I report a scam or violation?',
      answer: 'If you encounter suspicious activity, scams, or violations of our community guidelines, please report it immediately. You can use the "Report" button on any post, message, or profile, or contact our support team directly. We take all reports seriously and investigate promptly to maintain a safe community environment.',
      category: 'Safety',
      icon: <Shield className="w-5 h-5" />
    },
    {
      id: 'account-verification',
      question: 'How do I get my account verified?',
      answer: 'Account verification helps build trust in the community. To get verified, submit proof of your professional work (demo reel, IMDB credits, or industry references). Verified accounts get priority in job listings and access to exclusive features. The verification process typically takes 2-3 business days.',
      category: 'Account',
      icon: <Users className="w-5 h-5" />
    },
    {
      id: 'live-streaming',
      question: 'Can I host live streams on Indiestry?',
      answer: 'Yes! Indiestry has built-in live streaming capabilities. You can host workshops, Q&A sessions, live auditions, or casual community hangouts. Our streaming platform supports screen sharing, chat interactions, and recording. All streamers must follow our community guidelines and streaming terms of service.',
      category: 'Streaming',
      icon: <HelpCircle className="w-5 h-5" />
    }
  ];

  const categories = ['All', 'General', 'Portfolio', 'Jobs', 'Education', 'Technical', 'Safety', 'Account', 'Streaming'];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredItems = faqItems.filter(item => {
    const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleItem = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
      <PageHero
        title="Frequently Asked Questions"
        subtitle="Find answers to common questions about Indiestry, voice acting opportunities, and our platform features."
        variant="strip"
        background="slate-blur"
        titleGradient="from-blue-300 to-indigo-400"
      />

      {/* Main Content */}
      <section className="py-8">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Filters */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredItems.map((item) => (
              <article key={item.id} className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-blue-400">
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-white">{item.question}</h3>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${
                      expandedItems.includes(item.id) ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {expandedItems.includes(item.id) && (
                  <div className="px-6 pb-4">
                    <div className="border-t border-slate-700/50 pt-4">
                      <p className="text-slate-300 leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>

          {/* No Results */}
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No questions found</h3>
              <p className="text-slate-400 mb-6">Try adjusting your search terms or category filter.</p>
              <Button 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('All');
                }}
                className="bg-blue-600 hover:bg-blue-500"
              >
                Clear Filters
              </Button>
            </div>
          )}

          {/* Contact Support */}
          <div className="mt-12 bg-slate-800/30 backdrop-blur-sm rounded-xl p-8 border border-slate-700/50 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Still have questions?</h3>
            <p className="text-slate-300 mb-6">
              Can&apos;t find what you&apos;re looking for? Our support team is here to help!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact">
                <Button className="bg-blue-600 hover:bg-blue-500 text-white">
                  Contact Support
                </Button>
              </Link>
              <Link href="/community">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  Ask the Community
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
} 